import { ChatCircleText } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useApi } from "../../api";
import { useDocumentTitle } from "@/lib/document-title";

export const Route = createFileRoute("/_app/w/$slug/threads")({
  component: ThreadsPage,
});

function ThreadsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { slug } = useParams({ from: "/_app/w/$slug/threads" });

  const workspaces = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => api.listWorkspaces(),
  });
  const workspace = workspaces.data?.find((w) => w.slug === slug);
  useDocumentTitle(["Threads", workspace?.name]);

  const threads = useQuery({
    queryKey: ["threads", workspace?.id],
    queryFn: () => api.listThreads(workspace!.id),
    enabled: !!workspace,
  });

  return (
    <div className="mx-auto h-full max-w-[900px] overflow-auto px-8 py-10">
      <header className="mb-8 flex flex-col gap-1">
        <p className="meta-strip">Workspace · {workspace?.slug ?? "—"}</p>
        <h1 className="text-3xl font-semibold tracking-tight">Threads</h1>
        <p className="text-sm text-muted-foreground">
          Conversations with the agent. Open a canvas to start a new one — each canvas gets its own
          thread.
        </p>
      </header>

      {threads.isLoading ? (
        <div className="flex flex-col gap-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      ) : threads.data && threads.data.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead className="w-32">Canvas</TableHead>
              <TableHead className="w-44 text-right">Last message</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {threads.data.map((thread) => (
              <TableRow
                key={thread.id}
                className="cursor-pointer"
                onClick={() => {
                  if (thread.canvas_id) {
                    void navigate({
                      to: "/w/$slug/c/$canvasId",
                      params: { slug, canvasId: thread.canvas_id },
                    });
                  }
                }}
              >
                <TableCell className="font-medium">{thread.title}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {thread.canvas_id ? thread.canvas_id.slice(-6) : "—"}
                </TableCell>
                <TableCell className="text-right font-mono text-xs text-muted-foreground">
                  {thread.last_message_at ? new Date(thread.last_message_at).toLocaleString() : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Empty className="bg-card">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ChatCircleText weight="duotone" />
            </EmptyMedia>
            <EmptyTitle>No threads yet</EmptyTitle>
            <EmptyDescription>
              Open a canvas and send a message to start a conversation. Threads persist all agent
              events.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
    </div>
  );
}
